import * as React from "react";
import {
  SimpleForm,
  NumberInput,
  useNotify,
  required,
  maxValue,
} from "react-admin";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  CardContent,
  Button,
  Card,
  Stack,
  Container,
  Box,
  TableContainer,
  Paper,
  Grid,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation } from "react-router-dom";
import { GdprBanner } from "./GdprBanner";
import { COUNT_BINS_MAX, integerOnly } from "../validators";
import { AppointmentList } from "./partials/AppointmentList";

const API_URL =
  process.env.REACT_APP_API_URL || "https://aihubcy.hubency.com/api";

interface InventoryRecord {
  id: string;
  fullBinsCount: number;
  emptyBinsCount: number;
  createdAt: string;
  createdFrom: string;
}

interface ProviderResponse {
  provider: { _id: string; name: string; inventory: InventoryRecord };
}

interface AppointmentRecord {
  _id: string;
  outlet: string;
  scheduledAt: Date | string;
  status: string;
  quantityBin?: number;
  quantityBinToBeCollected?: number;
  executionDate?: Date | string;
}

interface AppontmentRespose {
  data: AppointmentRecord[];
}

interface InventoryListResponse {
  data: InventoryRecord[];
  total: number;
}

const createdFromChoises = [
  { id: "MANUALLY_ADMIN", name: "Administrateur" },
  { id: "MANUALLY_PROVIDER", name: "Vous" },
  { id: "OUTLET", name: "Exutoire" },
  { id: "ORDER", name: "Bon de commande" },
];

const createdFromLabels: Record<string, string> = createdFromChoises.reduce(
  (acc, { id, name }) => ({ ...acc, [id]: name }),
  {}
);

export const ExternalsProviderInventoryCreate: React.FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const inviteToken = params.get("inviteToken") || "";
  const notify = useNotify();

  const [loadingProvider, setLoadingProvider] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [providerName, setProviderName] = React.useState("");
  const [providerId, setProviderId] = React.useState("");

  const [inventory, setInventory] = React.useState<InventoryRecord[]>([]);
  const [latestInventory, setLatestInventory] =
    React.useState<InventoryRecord>();
  const [appointments, setAppointments] = React.useState<AppointmentRecord[]>(
    []
  );
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [loadingInv, setLoadingInv] = React.useState(false);

  const [showForm, setShowForm] = React.useState(false);
  const [thankYouOpen, setThankYouOpen] = React.useState(false);
  const [certified, setCertified] = React.useState(false);

  const loadInventory = React.useCallback(
    async (pageNum = page, perPage = rowsPerPage) => {
      setLoadingInv(true);
      try {
        const res = await fetch(
          `${API_URL}/provider-inventory/invite/list?inviteToken=${inviteToken}` +
            `&page=${pageNum + 1}&limit=${perPage}`
        );
        if (!res.ok) throw new Error(`Échec liste (${res.status})`);
        const json: InventoryListResponse = await res.json();
        setInventory(json.data);
        if (json.data.length) {
          setLatestInventory(json.data[0]);
        }
        setTotal(json.total);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoadingInv(false);
      }
    },
    [inviteToken, page, rowsPerPage]
  );

  React.useEffect(() => {
    if (!inviteToken) {
      setError("Lien invalide");
      setLoadingProvider(false);
      return;
    }
    fetch(
      `${API_URL}/provider-inventory/invite/provider?inviteToken=${inviteToken}`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Échec validation (${res.status})`);
        return res.json() as Promise<ProviderResponse>;
      })
      .then(({ provider }) => {
        setProviderName(provider.name);
        setLatestInventory(provider.inventory);
        setProviderId(provider._id);
      })
      .catch((err) => setError(err.message));

    fetch(`${API_URL}/appointments?inviteToken=${inviteToken}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Échec validation (${res.status})`);
        return res.json() as Promise<AppontmentRespose>;
      })
      .then(({ data }) => {
        setAppointments(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingProvider(false));
  }, [inviteToken]);

  React.useEffect(() => {
    if (providerId) loadInventory(0, rowsPerPage);
  }, [providerId, loadInventory, rowsPerPage]);

  const onSubmit = async (values: any) => {
    try {
      const res = await fetch(`${API_URL}/provider-inventory/${providerId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Invite-Token": inviteToken,
        },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await res.text());
      notify("Votre inventaire a été bien transmis", { type: "info" });
      setShowForm(false);
      setPage(0);
      await loadInventory(0, rowsPerPage);
      setThankYouOpen(true);
    } catch (err: any) {
      notify(`Erreur : ${err.message}`, { type: "warning" });
    }
  };

  if (loadingProvider)
    return <CircularProgress sx={{ display: "block", m: "auto" }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );

  return (
    <>
      <title>Hubency Carebag - Inventaire prestataire</title>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Bonjour {providerName}
        </Typography>
        <Typography variant="subtitle1" align="center" sx={{ mb: 3 }}>
          Bienvenue sur votre espace prestataire. Ici, vous pouvez suivre et
          mettre à jour vos inventaires de caisses directement depuis la
          plateforme.
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Card sx={{ flex: 1, textAlign: "center" }}>
            <CardContent>
              <Typography variant="subtitle2">
                Caisses pleines sur votre HUB
              </Typography>
              <Typography variant="h6">
                {latestInventory?.fullBinsCount || 0}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, textAlign: "center" }}>
            <CardContent>
              <Typography variant="subtitle2">
                Caisses vides sur votre HUB
              </Typography>
              <Typography variant="h6">
                {latestInventory?.emptyBinsCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Alert severity="warning" sx={{ mb: 3 }}>
          ⚠️ Un seul inventaire par jour est nécessaire, merci de ne pas en
          créer plusieurs.
        </Alert>

        <Box textAlign="center" sx={{ mt: 3, mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowForm(true)}
          >
            Mettre à jour mon inventaire
          </Button>
        </Box>

        {loadingInv ? (
          <CircularProgress sx={{ display: "block", m: "auto" }} />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Variation de votre stock
              </Typography>

              <Paper sx={{ p: 2, mt: 2 }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Caisses pleines</TableCell>
                        <TableCell align="center">Caisses vides</TableCell>
                        <TableCell align="right">Ajouté par</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventory.map((rec) => (
                        <TableRow key={rec.id}>
                          <TableCell>
                            {new Date(rec.createdAt).toLocaleString("fr-FR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </TableCell>
                          <TableCell align="center">
                            {rec.fullBinsCount}
                          </TableCell>
                          <TableCell align="center">
                            {rec.emptyBinsCount}
                          </TableCell>
                          <TableCell align="right">
                            {createdFromLabels[rec.createdFrom] ||
                              rec.createdFrom}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => {
                      setPage(newPage);
                      loadInventory(newPage, rowsPerPage);
                    }}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      const rp = parseInt(e.target.value, 10);
                      setRowsPerPage(rp);
                      setPage(0);
                      loadInventory(0, rp);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </TableContainer>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                List de vos RDV
                <AppointmentList appointments={appointments} />
              </Typography>
            </Grid>
          </Grid>
        )}
      </Container>

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setCertified(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nouvel inventaire</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Après le scan, un inventaire sera ajouté automatiquement par rapport
            au dernier inventaire.
          </Alert>

          <SimpleForm onSubmit={onSubmit} toolbar={false}>
            <NumberInput
              source="fullBinsCount"
              label="Caisses pleines"
              validate={[
                required("La quantité de caisses est requise"),
                maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <NumberInput
              source="emptyBinsCount"
              label="Caisses vides"
              validate={[
                required("La quantité de caisses est requise"),
                maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <NumberInput
              source="providerId"
              defaultValue={providerId}
              style={{ display: "none" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={certified}
                  onChange={(e) => setCertified(e.target.checked)}
                />
              }
              label="Je certifie que les données saisies sont exactes."
              sx={{ mt: 2 }}
            />
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowForm(false);
              setCertified(false);
            }}
            color="warning"
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Annuler
          </Button>
          <Button
            onClick={() =>
              document
                .querySelector("form")
                ?.dispatchEvent(
                  new Event("submit", { cancelable: true, bubbles: true })
                )
            }
            color="primary"
            variant="contained"
            disabled={!certified}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merci */}
      <Dialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        aria-labelledby="thank-you-dialog"
      >
        <DialogTitle id="thank-you-dialog">Merci 🎉</DialogTitle>
        <DialogContent>
          <Typography>Votre inventaire a bien été transmis.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            L’équipe Hubency vous souhaite une excellente journée 🌿
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThankYouOpen(false)} autoFocus>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <GdprBanner />
    </>
  );
};
